import { APIURL, apiGet, appendFlatpickrBaseStyles, cancelSelectEvent, getCSSStyles, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, isEmptyWidgetData } from '../../helpers';
import type {  WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import flatpickr from 'flatpickr';
import { format } from 'date-fns';
import cryptoEventsCalendarStyleText from './../../assets/css/showcase/cryptoEventsCalendar.css?raw';

/** Impact filter keys (match API `impact` lowercased). */
type ImpactFilterKey = 'high' | 'medium' | 'low';

export class TraderverseCryptoEventsCalendar {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private widgetResizeObserver: ResizeObserver | null = null;
  private calendarInstance: flatpickr.Instance | null = null;
  private rangeStart: string;
  private rangeEnd: string;
  private selectedDate: string;
  private activeFilters: Set<ImpactFilterKey> = new Set(['high', 'medium', 'low']);
  private eventsByDate: Map<string, any[]> = new Map();
  private lastResponse: any | null = null;
  private filtersOutsideClickHandler: ((event: MouseEvent) => void) | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.rangeStart = this.config.start_date ?? '2026-05-10';
    this.rangeEnd = this.config.end_date ?? '2026-05-16';
    this.selectedDate = this.rangeStart;
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '420px',
      autosize: true,
      hideSymbol: true,
    };
  }

  private async init(): Promise<void> {
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    await this.fetchEconomicEvents();
    this.initCalendar();
    this.setupEventListeners();
    this.updateDisplay();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

 private async fetchEconomicEvents(): Promise<void> {
  try {
    const response = await apiGet({
      url: `${APIURL}/v1/discover/economic_events`,
      query: {
        start_date: this.rangeStart,
        end_date: this.rangeEnd,
      },
    });

    if (!response.ok) throw new Error(`economic_events ${response.status}`);

    this.lastResponse = await response.json();

    const rows = Array.isArray(this.lastResponse?.data)
      ? this.lastResponse.data
      : [];

    const container = this.shadowRoot.querySelector(
      '#cryptoEventsCalendarContainer'
    ) as HTMLElement;

    if (rows.length > 0) {
      container?.setAttribute(
        'data-testing-id',
        'crypto-events-calendar'
      );
    } else {
      container?.removeAttribute('data-testing-id');
    }

    this.rebuildEventsByDate(rows);
    this.selectInitialDate(rows);
    this.emit('dataUpdate', this.lastResponse);

  } catch (e) {
    console.error('TraderverseCryptoEventsCalendar: fetch failed', e);

    const container = this.shadowRoot.querySelector(
      '#cryptoEventsCalendarContainer'
    ) as HTMLElement;

    container?.removeAttribute('data-testing-id');

    this.lastResponse = { data: [] };
    this.rebuildEventsByDate([]);
  }
}

  private selectInitialDate(rows: any[]): void {
    if (!rows.length) return;
    const keys = Array.from(this.eventsByDate.keys()).sort();
    if (!keys.length) return;
    if (!this.eventsByDate.has(this.selectedDate)) {
      this.selectedDate = keys[0];
    }
  }

  private dateKeyFromApi(isoDate: string): string {
    const t = isoDate.indexOf('T');
    return t > 0 ? isoDate.slice(0, t) : isoDate.slice(0, 10);
  }

  private rebuildEventsByDate(rows: any[]): void {
    const map = new Map<string, any[]>();
    for (const row of rows) {
      if (!row?.date) continue;
      const key = this.dateKeyFromApi(row.date);
      const list = map.get(key) ?? [];
      list.push(row);
      map.set(key, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
    this.eventsByDate = map;
  }

  private passesImpactFilter(item: any): boolean {
    if (this.activeFilters.size === 0) return false;
    const raw = (item.impact || '').toLowerCase();
    if (raw === 'high' || raw === 'medium' || raw === 'low') {
      return this.activeFilters.has(raw);
    }
    return this.activeFilters.size === 3;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }

    appendFlatpickrBaseStyles(this.shadowRoot);

    const localStyle = document.createElement('style');
    localStyle.textContent = cryptoEventsCalendarStyleText;
    this.shadowRoot.appendChild(localStyle);
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} ${this.config.title || 'Event Calendar'}</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <div class="item">
              <button type="button" id="calendarFiltersBtn" class="filters-toggle calendarFilterBtn" aria-label="Filters" aria-expanded="false">
                ${this.getFilterSvg()}
              </button>
            </div>
          </div>
        </div>
        <div class="inBody">
          <div class="calendarFiltersPanel filtersBox sideBar hidden" id="calendarFiltersPanel">
            <div class="item">
              <label>Impact</label>
              <div class="calendarFilterChips">
                <button type="button" class="filterChip active" data-filter="high">High</button>
                <button type="button" class="filterChip active" data-filter="medium">Medium</button>
                <button type="button" class="filterChip active" data-filter="low">Low</button>
              </div>
            </div>
          </div>
          <div class="chartDv" id="cryptoEventsCalendarContainer">
            <div class="cryptoEventsCalendarMain">
              <div class="calendarTopBar">
                <div class="calendarMonthNav">
                  <button type="button" id="calendarPrevBtn" class="calendarNavBtn calendarNavBtn--prev" aria-label="Previous month">
                    ${this.getChevronSvg('left')}
                  </button>
                  <div class="calendarMonthLabel" id="calendarMonthLabel">April 2026</div>
                  <button type="button" id="calendarNextBtn" class="calendarNavBtn calendarNavBtn--next" aria-label="Next month">
                    ${this.getChevronSvg('right')}
                  </button>
                </div>
              </div>
              <div class="calendarPanel">
                <input id="cryptoEventsCalendarInput" class="calendarInput" type="text" aria-hidden="true" tabindex="-1" />
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
  }

  private initCalendar(): void {
    const input = this.shadowRoot.querySelector('#cryptoEventsCalendarInput') as HTMLInputElement | null;
    if (!input) return;

    this.calendarInstance = flatpickr(input, {
      inline: true,
      defaultDate: this.selectedDate,
      monthSelectorType: 'static',
      disableMobile: true,
      clickOpens: false,
      prevArrow: '',
      nextArrow: '',
      onReady: (_, __, instance) => {
        instance.calendarContainer.classList.add('tv-crypto-calendar');
        this.syncMonthLabel();
        this.decorateVisibleDays();
      },
      onMonthChange: () => {
        this.syncMonthLabel();
        this.decorateVisibleDays();
      },
      onYearChange: () => {
        this.syncMonthLabel();
        this.decorateVisibleDays();
      },
      onChange: (selectedDates) => {
        if (selectedDates[0]) {
          this.selectedDate = format(selectedDates[0], 'yyyy-MM-dd');
        }
        this.decorateVisibleDays();
      },
      onDayCreate: (_, __, ___, dayElem) => {
        const dayDate = (dayElem as any).dateObj as Date | undefined;
        if (!dayDate) return;
        const key = format(dayDate, 'yyyy-MM-dd');
        dayElem.setAttribute('data-date-key', key);
      },
    });

    if (this.selectedDate) {
      this.calendarInstance.setDate(this.selectedDate, false);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const prevBtn = this.shadowRoot.querySelector('#calendarPrevBtn') as HTMLButtonElement | null;
    const nextBtn = this.shadowRoot.querySelector('#calendarNextBtn') as HTMLButtonElement | null;
    const filtersBtn = this.shadowRoot.querySelector('#calendarFiltersBtn') as HTMLButtonElement | null;
    const filtersPanel = this.shadowRoot.querySelector('#calendarFiltersPanel') as HTMLElement | null;
    const filterChips = Array.from(this.shadowRoot.querySelectorAll('.filterChip')) as HTMLButtonElement[];

    prevBtn?.addEventListener('click', () => {
      this.calendarInstance?.changeMonth(-1);
      this.decorateVisibleDays();
    });

    nextBtn?.addEventListener('click', () => {
      this.calendarInstance?.changeMonth(1);
      this.decorateVisibleDays();
    });

    filtersBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      filtersPanel?.classList.toggle('hidden');
      const isOpen = Boolean(filtersPanel && !filtersPanel.classList.contains('hidden'));
      filtersBtn.classList.toggle('active', isOpen);
      filtersBtn.setAttribute('aria-expanded', String(isOpen));
    });
    filtersPanel?.addEventListener('click', (event) => event.stopPropagation());
    filtersPanel?.addEventListener('pointerdown', (event) => event.stopPropagation());

    if (filtersBtn && filtersPanel) {
      this.filtersOutsideClickHandler = (event: MouseEvent) => {
        const path: unknown[] = typeof event.composedPath === 'function' ? event.composedPath() : [];
        if (path.includes(filtersBtn) || path.includes(filtersPanel)) return;
        filtersPanel.classList.add('hidden');
        filtersBtn.classList.remove('active');
        filtersBtn.setAttribute('aria-expanded', 'false');
      };
      document.addEventListener('click', this.filtersOutsideClickHandler);
    }

    filterChips.forEach((chip) => {
      chip.addEventListener('click', (event) => {
        event.stopPropagation();
        const filter = chip.dataset.filter as ImpactFilterKey | undefined;
        if (!filter) return;
        if (this.activeFilters.has(filter)) {
          this.activeFilters.delete(filter);
          chip.classList.remove('active');
        } else {
          this.activeFilters.add(filter);
          chip.classList.add('active');
        }
        this.decorateVisibleDays();
      });
    });
  }

  private updateDisplay(): void {
    const rows = Array.isArray(this.lastResponse?.data) ? this.lastResponse.data : [];
    if (isEmptyWidgetData(rows)) return;
    if (this.calendarInstance && this.selectedDate) {
      this.calendarInstance.setDate(this.selectedDate, false);
    }
    this.syncMonthLabel();
    this.decorateVisibleDays();
  }

  private syncMonthLabel(): void {
    if (!this.calendarInstance) return;
    const label = this.shadowRoot.querySelector('#calendarMonthLabel') as HTMLElement | null;
    if (!label) return;
    const current = new Date(this.calendarInstance.currentYear, this.calendarInstance.currentMonth, 1);
    label.textContent = format(current, 'MMMM yyyy');
  }

  private decorateVisibleDays(): void {
    if (!this.calendarInstance) return;
    const dayNodes = Array.from(
      this.calendarInstance.calendarContainer.querySelectorAll('.flatpickr-day')
    ) as HTMLElement[];

    dayNodes.forEach((day) => {
      day.classList.remove('tv-has-events', 'tv-selected-event-day');
      day.querySelector('.tv-event-badge')?.remove();

      const dayDate = (day as any).dateObj as Date | undefined;
      if (!dayDate) return;

      const key = format(dayDate, 'yyyy-MM-dd');
      const count = this.getVisibleEventCount(key);

      if (count > 0) {
        day.classList.add('tv-has-events');
        const badge = document.createElement('span');
        badge.className = 'tv-event-badge';
        badge.textContent = String(count);
        day.appendChild(badge);
      }

      if (key === this.selectedDate && count > 0) {
        day.classList.add('tv-selected-event-day');
      }
    });
  }

  private getVisibleEventCount(dateKey: string): number {
    const events = this.eventsByDate.get(dateKey) ?? [];
    return events.filter((item) => this.passesImpactFilter(item)).length;
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.filtersOutsideClickHandler) {
      document.removeEventListener('click', this.filtersOutsideClickHandler);
      this.filtersOutsideClickHandler = null;
    }
    this.calendarInstance?.destroy();
    this.calendarInstance = null;
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((handler) => handler(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

  private getChevronSvg(direction: 'left' | 'right'): string {
    return direction === 'left'
      ? `<svg class="calendarNavIcon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M9.75 3.25L5 8L9.75 12.75" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`
      : `<svg class="calendarNavIcon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6.25 3.25L11 8L6.25 12.75" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  private getFilterSvg(): string {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
  }
}

declare global {
  interface Window { TraderverseCryptoEventsCalendar?: typeof TraderverseCryptoEventsCalendar; }
}

if (typeof window !== 'undefined') {
  window.TraderverseCryptoEventsCalendar = TraderverseCryptoEventsCalendar;
}
